import CloseIcon from '@mui/icons-material/Close';
import FormActionButtons from '../components/FormActionButtons';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Box, Button, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import { DataAPIContext, DELETE, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import ConfirmationDialog from '../components/ConfirmationDialog';
import images from '../components/images';
import PageMeta from '../components/PageMeta';
import { RecordDetails, RecordDetailsContext } from '../components/RecordDetails';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton, formatPrice } from '../utils';
import { formatDayNumber, getDayNumber } from './utils';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => {
    console.log('theme', theme);
    return {
        root: {
            paddingTop: '1.5rem',
        },
        scrollableRoot: {
            height: `calc(${theme.cp.contentHeight} - 3.35rem)`,
            overflowY: 'auto',
            [theme.breakpoints.down('sm')]: {
                height: `calc(${theme.cp.contentHeight} - 7rem)`,

            },
        },
        field: {
            padding: theme.spacing(1),
        },
        label: {
            color: grey[600],
            fontSize: '12px',
            lineHeight: '16px',
        },
        value: {
            color: '#202020',
            fontSize: '16px',
            lineHeight: '24px',
        },
        previewImage: {
            borderRadius: '10px',
        },
        dayNumber: {
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontSize: 'x-small',
        },
        itineraryTitle: {},
        itineraryDescription: {
            color: grey[600],
            maxWidth: '400px',
            overflowX: 'hidden',
        },
        bookingUrl: {
            whiteSpace: 'nowrap',
            paddingBottom: '1em',
            overflowX: 'hidden',
        },
    };
});

const EMDASH = String.fromCharCode(8212);

const PreviewField = (props) => {
    const classes = useStyles();
    return (
        <div className={classes.field}>
            <div className={classes.label}>{props.label}</div>
            <div className={classes.value}>{props.value || EMDASH}</div>
        </div>
    );
};

const CPImage = (props) => {
    console.log('Image', props);
    const nximg = props.placeholder || <div className="non-existing-image-placeholder" />;
    const { settings } = React.useContext(ContextStore);

    if (!props.src) {
        return nximg;
    }
    const fit = props.fit || 'cover';
    const preset = `${fit}_${props.width}x${props.height}`;
    const imageURL = images.url(settings.images.base, props.src, preset);
    console.log('Image', imageURL);
    if (!imageURL) {
        return nximg;
    }
    const imageStyle = {};
    if (props.roundCorners) {
        imageStyle.borderRadius = `${props.roundCorners}px`;
    }
    return <Image src={imageURL} cover={true} aspectRatio={props.width / props.height} height={props.height} style={imageStyle} />;
};

const PreviewImage = (props) => {
    const classes = useStyles();
    const img = props.cover_image || (props.list ? props?.list[props.idx || 0]?.image : null);
    if (!img) {
        return <div />;
    }
    console.log('PreviewImage', props, img);
    return <CPImage src={img} fit="cover" width={368} height={250} className={props.className || classes.previewImage} roundCorners={10} />;
};

const ItineraryPreviewPanelForm = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { currencyMap, hasPerm } = React.useContext(ContextStore);

    const history = useHistory();
    const classes = useStyles();
    const deleteRef = React.useRef(null);
    const editRef = React.useRef(null);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleEdit = (ev) => {
        console.log('editing', props.data.id);
        history.push(`/explore/itineraries/${props.data.id}`);
    };
    const doDelete = (ev) => {
        console.log('deleting', props.data.id);
        disableMuiButton(deleteRef.current);
        (async () => {
            try {
                const res = await dataAPI(DELETE, props.resource, { id: props.data.id });
                console.log('delete', res);
                props.onClose && props.onClose();
                enqueueSnackbar('Itinerary deleted', { variant: 'default' });
                props.onDelete && props.onDelete();
                history.push(`/explore/itineraries`);
            } finally {
                if (deleteRef && deleteRef.current) {
                    enableMuiButton(deleteRef.current);
                }
            }
        })();
    };

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_itinerary');
    };

    const getPriceString = (it) => {
        const cur = it.currency ? currencyMap[it.currency] : currencyMap[1]; // assume 1 is USD
        if (!it.cost) {
            return null;
        }
        let value = formatPrice(it.cost, cur);
        if (it.cost_prefix) {
            value = `${it.cost_prefix} ${value}`;
        }
        if (it.cost_unit) {
            value = `${value} ${it.cost_unit}`;
        }
        return value;
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_itinerary':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    // //Duplicate option
    const [syncActive, setSyncActive] = React.useState(false);
    const handleDuplicate = (ev) => {
        console.log('duplicating', props.data.id);
        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(UPDATE, props.resource, {
                    urlSuffix: 'duplicate/',
                    id: props.data.id,
                });
                // console.log('duplicate', res);
                enqueueSnackbar('Itinerary duplicated', {
                    variant: 'success',
                    action: (key) => (
                        <React.Fragment>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                    history.push(`/explore/itineraries/${res.data.id}`);
                                }}
                            >
                                Open it
                            </Button>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                }}
                            >
                                Dismiss
                            </Button>
                        </React.Fragment>
                    ),
                });
            } catch (e) {
                enqueueSnackbar('Error duplicating itinerary', { variant: 'error' });
                // console.error("Error duplicating itinerary", e);
            } finally {
                setSyncActive(false);
            }
        })();
    };

    return (
        <div className={classes.root}>
            <PageMeta title={`Itinerary - ${props.data.name}`} />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <div className={classes.scrollableRoot}>
                <Grid container direction="row" mb={2}>
                    <Grid item xs={8}>
                        <Typography variant="h6">Itinerary Details</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                        <Tooltip title="Close">
                            <IconButton
                                aria-label="close-preview"
                                onClick={props.onClose}
                                size="small"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.87)' },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <PreviewImage cover_image={props.data.cover_image} list={props.data.images} preset="cover_368x250" />
                <div className={classes.field}>
                    {props.data.duration_display ? (
                        <Typography color="primary" variant="body2" className={classes.dayNumber}>
                            {props.data.duration_display}
                        </Typography>
                    ) : (
                        <Typography color="primary" variant="body2" className={classes.dayNumber}>
                            {formatDayNumber(getDayNumber(props.data))}
                        </Typography>
                    )}
                    <Typography variant="h6" className={classes.itineraryTitle}>
                        {props.data.name}
                    </Typography>
                    <Typography variant="body2" className={classes.itineraryDescription}>
                        {props.data.description}
                    </Typography>
                </div>

                <PreviewField label="Status" value={props.data.published ? 'Published' : 'Draft'} />
                <PreviewField label="Regions" value={props.data.regions.map((r) => r.name).join(', ')} />
                <PreviewField label="Trip Types" value={props.data.trip_types.map((t) => t.name).join(', ')} />
                <PreviewField label="Cost" value={getPriceString(props.data, currencyMap)} />
                <div className={classes.field}>
                    <div className={classes.label}>Booking URL</div>
                    <div className={classes.bookingUrl}>{props.data.booking_url ? <Link href={props.data.booking_url}>{props.data.booking_url}</Link> : EMDASH}</div>
                </div>
            </div>
            <Box
                mt={4}
                style={{
                    position: 'sticky',
                }}
            >
                {/* CP-8427: bottom right, primary action on the right (Martina): [DUPLICATE] [DELETE] [EDIT]. */}
                <FormActionButtons
                    size="medium"
                    leadingActions={
                        hasPerm('itinerary', 'write') && (
                            <Button disabled={syncActive} color="primary" onClick={handleDuplicate}>
                                <FileCopyOutlinedIcon />
                                &nbsp;Duplicate
                            </Button>
                        )
                    }
                    onDelete={handleDelete}
                    deletePerm="itinerary.delete"
                    deleteRef={deleteRef}
                    onSubmit={handleEdit}
                    submitRef={editRef}
                    submitLabel="Edit"
                    submitIcon={<EditIcon />}
                />
            </Box>
        </div>
    );
};

const ItineraryPreviewPanel = (props) => {
    const resource = 'itinerary';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => <ItineraryPreviewPanelForm {...props} resource={resource} id={id} data={res.data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};
export default ItineraryPreviewPanel;
